import * as turf from "@turf/turf"
import proj4 from "proj4"

import {
  CircleShapeType,
  CoordinatesType,
  LayerCoordinatesType,
  PositionType,
  ShapeFeatureType,
  ShapeType,
} from "../types/shape"
import {
  drawInscribedPolygon,
  isRequiredConvexPolygon,
} from "./configurationHelper"

import { wetzls } from "./innerCircleCalculator"
import {
  InterferometricStackType,
  ProductImageStacType,
  SearchResultElementType,
} from "../types/product"
import {
  AdvancedSearchConfigurationsType,
  ConfigurationsType,
} from "../types/configurations"
import { isInterferometricStackSearchResultElement } from "./searchResultsHelper"
import { getProductImageFromInterferometricStack } from "./interferometricStackHelper"
import { getCoordinatesFromGeoJSON } from "./geoJSONHelper"
import { getConvexHullCoordinatesFrom } from "./coordinatesHelper"
import { warningToastNotification } from "./toastNotificationHelper"
import translations from "../translations"
import {AppParametersType} from "../types/app";

export const convertDrawElementIntoShape = (
  configurations: ConfigurationsType,
  drawElement,
  formValues: any,
  language: string
): ShapeType => {
  const drawElementLayer = drawElement.layer.toGeoJSON()
  let shape: ShapeType = {
    layerType: drawElement.layerType,
    layerId: drawElement.layer._leaflet_id,
    layer: [],
    inscribedPolygon: null,
    added_on_time: Date.now() + 1000,
    area: 0,
  }

  let layerCoordinates
  if (drawElement.layerType === "circle") {
    const circle_center = drawElementLayer.geometry.coordinates as PositionType
    const circle_radius = drawElement.layer.getRadius()
    shape = {
      ...shape,
      circle_center,
      circle_radius,
      layer: [
        convertCircleToConvexPolygonCoordinates(circle_center, circle_radius),
      ],
      area: Math.PI * circle_radius * circle_radius,
    }
  } else {
    layerCoordinates = drawElementLayer.geometry
      .coordinates as LayerCoordinatesType
    layerCoordinates =
      drawElement.layerType === "polygon"
        ? [
            convertToPolygonCoordinates(
              configurations,
              layerCoordinates[0],
              formValues,
              language
            ),
          ]
        : layerCoordinates
    shape = {
      ...shape,
      layer: layerCoordinates,
      area: calculatePolygonAreaFromCoordinates(layerCoordinates[0]),
    }
  }

  if (
    (drawElement?.layerType === "polygon" ||
      drawElement?.layerType === "rectangle") &&
      drawInscribedPolygon(configurations)
  ) {
    const latlngs = (
      drawElement.layer.editing.latlngs
        ? drawElement.layer.editing.latlngs[0][0]
        : drawElement.layer._latlngs[0]
    ).map(({ lat, lng }) => ({
      x: lng,
      y: lat,
    }))
    const points = wetzls(latlngs)
    const convertedRadius = Math.max(
      ...latlngs.map((elem) =>
        calculateDistanceBetween(elem.x, elem.y, points.x, points.y)
      )
    )

    const newLastShape = {
      ...shape,
      ...convertCircleCoordinatesIntoShape(
        parseFloat(points.y),
        parseFloat(points.x),
        convertedRadius
      ),
    }

    shape = {
      ...newLastShape,
      inscribedPolygon: {
        layer: [...shape.layer[0].map(([x, y]) => [y, x])],
      },
    }
  }
  return shape
}

export const calculateDistanceBetween: (
  x1: number,
  y1: number,
  x2: number,
  y2: number
) => number = (x1, y1, x2, y2) => {
  // return distance in meters
  const lon1 = toRadian(x1),
    lat1 = toRadian(y1),
    lon2 = toRadian(x2),
    lat2 = toRadian(y2)

  const deltaLat = lat2 - lat1
  const deltaLon = lon2 - lon1

  const a =
    Math.pow(Math.sin(deltaLat / 2), 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.pow(Math.sin(deltaLon / 2), 2)
  const c = 2 * Math.asin(Math.sqrt(a))
  const EARTH_RADIUS = 6371
  const toReturn = c * EARTH_RADIUS * 1000
  console.log(toReturn)
  return toReturn
}
export const toRadian = (degree) => {
  return (degree * Math.PI) / 180
}

export const convertRectangleCoordinatesIntoShape = (
  latA: number,
  lngA: number,
  latB: number,
  lngB: number
): ShapeType => {
  let shape: ShapeType = {
    layerType: "rectangle",
    layerId: 1,
    layer: [],
    added_on_time: Date.now() + 1000,
    area: 0,
  }

  const line = turf.lineString([
    [latA, lngA],
    [latB, lngB],
  ])
  const bbox = turf.bbox(line)
  const polygon = turf.bboxPolygon(bbox)
  const layerCord: LayerCoordinatesType = polygon.geometry
    ?.coordinates as LayerCoordinatesType

  const area = turf.area(polygon)

  shape = {
    ...shape,
    layer: layerCord,
    area: area,
  }

  return shape
}

export const convertCircleCoordinatesIntoShape = (
  lat: number,
  lng: number,
  radius: number
): ShapeType => {
  let shape: ShapeType = {
    layerType: "circle",
    layerId: 1,
    layer: [],
    added_on_time: Date.now() + 1000,
    area: 0,
  }

  const circle_center = [lng, lat]
  const circle_radius = radius
  shape = {
    ...shape,
    circle_center,
    circle_radius,
    layer: [
      convertCircleToConvexPolygonCoordinates(circle_center, circle_radius),
    ],
    area: Math.PI * circle_radius * circle_radius,
  }

  return shape
}

export const convertPathStringIntoPolygonCoordinates = (
  configurations: ConfigurationsType,
  pathString: string,
  formValues: any,
  language: string
): CoordinatesType => {
  let polygonCoordinates: CoordinatesType = []
  if (pathString) {
    let newCoordinates = pathString.split(",").map((e) => {
      const [lat, lng] = e.split(" - ")
      if (!lat || !lng) {
        return [0, 0]
      }
      return [parseFloat(lat || "0"), parseFloat(lng || "0")]
    })

    if (newCoordinates && newCoordinates.length >= 2) {
      const firstCoordinates = newCoordinates[0]
      const lastCoordinates = newCoordinates[newCoordinates.length - 1]
      if (
        !(
          firstCoordinates[0] === lastCoordinates[0] &&
          firstCoordinates[1] === lastCoordinates[1]
        )
      ) {
        newCoordinates.push(firstCoordinates)
      }
    }

    if (newCoordinates && newCoordinates.length >= 4) {
      polygonCoordinates = convertToPolygonCoordinates(
        configurations,
        newCoordinates,
        formValues,
        language
      )
    }
  }

  return polygonCoordinates
}

export const convertPathStringIntoShape = (
  configurations: ConfigurationsType,
  pathString: string,
  lastShape: ShapeType,
  formValues: any,
  language: string
): ShapeType | null => {
  let shape: ShapeType | null = null
  const coordinates = convertPathStringIntoPolygonCoordinates(
    configurations,
    pathString,
    formValues,
    language
  )
  if (coordinates.length > 0) {
    shape = {
      ...lastShape,
      layer: [coordinates],
      layerType: "polygon",
      area: calculatePolygonAreaFromCoordinates(coordinates),
      added_on_time: Date.now() + 1000,
    }
  }

  return shape
}

//////////////////////////////////////////
////////////// POLYGON //////////////////
//////////////////////////////////////////
const convertCircleToConvexPolygonCoordinates = (
  center: PositionType,
  radius: number
): CoordinatesType => {
  let circle
  if (center && radius > 0) {
    circle = turf.circle(center, radius / 1000, {
      steps: 36,
      units: "kilometers" as const,
      properties: {},
    })
  }
  let coords: CoordinatesType = []
  if (
    circle &&
    circle.geometry &&
    circle.geometry.coordinates &&
    circle.geometry.coordinates[0]
  ) {
    coords = circle.geometry.coordinates[0]
  }
  return coords
}

export const isAPolygonShape = (shape: ShapeType): boolean => {
  return shape && ["polygon", "rectangle"].includes(shape.layerType)
}

export const isPolygonSelectionVisible = (formValues) => {
  return true
}

export const convertToPolygonCoordinates = (
  configurations: ConfigurationsType,
  coordinates: CoordinatesType,
  formValues: any,
  language: string
): CoordinatesType => {
  let polygonCoordinates = coordinates
  if (
    isRequiredConvexPolygon(configurations, formValues) &&
    isConcavePolygonCoordinates(polygonCoordinates)
  ) {
    const polygon = turf.polygon([coordinates])
    const turfConvexPolygonCoordinates = getCoordinatesFromGeoJSON(
      turf.convex(polygon)
    )
    if (!!turfConvexPolygonCoordinates) {
      let uniqCoordinates: any[] = [turfConvexPolygonCoordinates[0]]
      for (let i = 1; i < turfConvexPolygonCoordinates.length; i++) {
        if (
          !(
            turfConvexPolygonCoordinates[i][0] ===
              turfConvexPolygonCoordinates[i - 1][0] &&
            turfConvexPolygonCoordinates[i][1] ===
              turfConvexPolygonCoordinates[i - 1][1]
          )
        ) {
          uniqCoordinates.push(turfConvexPolygonCoordinates[i])
        }
      }
      polygonCoordinates = uniqCoordinates // remove duplicates
      warningToastNotification(
        translations("polygonModifiedShapeHelper", language)
      )
    }
  }

  return polygonCoordinates
}

export const isConcavePolygonCoordinates = (
  coordinates: CoordinatesType
): boolean => {
  const convexCoordinates = getConvexHullCoordinatesFrom(coordinates)
  return wasConcavePolygonBy(coordinates, convexCoordinates)
}

const wasConcavePolygonBy = (
  drawElementCoordinates: CoordinatesType,
  convexCoordinates: CoordinatesType
) => {
  let drawCoords = drawElementCoordinates.filter(onlyUniqueCoordinates)
  const convexCoords = convexCoordinates.filter(onlyUniqueCoordinates)
  let wasConcave = true
  if (drawCoords.length === convexCoords.length) {
    const startIndex = drawCoords.findIndex(
      (c) => c[0] === convexCoords[0][0] && c[1] === convexCoords[0][1]
    )
    if (startIndex > -1) {
      // reorder drawCoords coords to be similar to convexCoords
      if (startIndex > 0) {
        drawCoords = drawCoords
          .slice(startIndex)
          .concat(drawCoords.slice(0, startIndex))
      }

      const reversedDrawCoords = drawCoords
        .slice(0, 1)
        .concat(drawCoords.slice(1).reverse())
      wasConcave = !(
        drawCoords.every(
          (c, index) =>
            c[0] === convexCoords[index][0] && c[1] === convexCoords[index][1]
        ) ||
        reversedDrawCoords.every(
          (c, index) =>
            c[0] === convexCoords[index][0] && c[1] === convexCoords[index][1]
        )
      )
    }
  }
  return wasConcave
}

export const onlyUniqueCoordinates = (currentCoords, currentIndex, self) => {
  const index = self.findIndex(
    (coords) => coords[0] === currentCoords[0] && coords[1] === currentCoords[1]
  )
  return currentIndex === index
}

export const calculatePolygonAreaFromCoordinates = (
  coordinates: CoordinatesType
) => {
  const p1 = turf.polygon([coordinates])
  return turf.area(p1)
}

//////////////////////////////////////////
////////////// CIRCLE ////////////////////
//////////////////////////////////////////
export const isACircleShape = (shape: ShapeType): boolean => {
  return (
    shape &&
    shape.layerType === "circle" &&
    "circle_radius" in shape &&
    "circle_center" in shape
  )
}

export const isCircleSelectionVisible = (formValues) => {
  return true;
}

export const convertSelectedAreaInShapeFeature = (
  selectedArea: ShapeType
): ShapeFeatureType => {
  let shapeFeature: ShapeFeatureType = {
    type: "Feature",
    properties: null,
    geometry: {
      type: "Polygon",
      coordinates: selectedArea.layer,
    },
  }

  if (isACircleShape(selectedArea)) {
    const { circle_radius, circle_center } = selectedArea as CircleShapeType

    shapeFeature = {
      ...shapeFeature,
      properties: {
        radius: circle_radius,
      },
      geometry: {
        type: "Point",
        coordinates: circle_center,
      },
    }
  }

  return shapeFeature
}

export const getCoordinatesFromSearchResultElement = (
  configuration: ConfigurationsType,
  appParameters: AppParametersType,
  searchResultElement: SearchResultElementType,
  selectedProducts: ProductImageStacType[],
  selectedArea: ShapeType
): CoordinatesType =>
  isInterferometricStackSearchResultElement(searchResultElement)
    ? getCoordinatesFromInterferometricStackSearchResultElement(
        configuration as AdvancedSearchConfigurationsType,
        appParameters,
        searchResultElement as InterferometricStackType,
        selectedProducts,
        selectedArea
      )
    : getCoordinatesFromGeoJSON(searchResultElement as ProductImageStacType)

const getCoordinatesFromInterferometricStackSearchResultElement = (
  configurations: AdvancedSearchConfigurationsType,
  appParameters: AppParametersType,
  searchResultElement: InterferometricStackType,
  selectedProducts: ProductImageStacType[],
  selectedArea: ShapeType
): CoordinatesType => {
  return getCoordinatesFromGeoJSON(
    getProductImageFromInterferometricStack(
      configurations,
      appParameters,
      searchResultElement,
      selectedProducts,
      selectedArea
    )
  )
}

export const leafletToTurfCoordinates = (
  coords: CoordinatesType
): CoordinatesType => {
  return coords.map((coords) => leafletToTurfPosition(coords))
}

export const turfToLeafletCoordinates = (
  coords: CoordinatesType
): CoordinatesType => {
  return coords.map((coords) => turfToLeafletPosition(coords))
}

export const leafletToTurfPosition = (position: PositionType): PositionType => {
  return proj4("EPSG:4326", "EPSG:3857", position)
}

export const turfToLeafletPosition = (position: PositionType): PositionType => {
  return proj4("EPSG:3857", "EPSG:4326", position)
}

export const switchPosition = (pos: PositionType): PositionType => {
  return [pos[1], pos[0]]
}

export const getLatLongFormat = (coords: CoordinatesType): CoordinatesType => {
  return coords.map((e) => switchPosition(e))
}

export const getCoordinatesFromSelectedArea = (
  selectedArea: ShapeType
): CoordinatesType => {
  return (selectedArea && selectedArea.layer && selectedArea.layer[0]) || []
}

export const isPathStringValid = (pathString: string): boolean => {
  return /^([-+]?[0-9]*(\.[0-9]+)? - [-+]?[0-9]*(\.[0-9]+)?(, )?){4,}$/.test(
    pathString
  )
}

export const calculateBearingAngleBetween = (
  firstPointCoords: PositionType,
  secondPointCoords: PositionType
): number => {
  return turf.bearing(
    turf.point(firstPointCoords),
    turf.point(secondPointCoords)
  )
}
